import type { ComponentDocs } from '../component-docs.js';

/**
 * What `<caos-record-field-inspector>` says about itself.
 *
 * A record page shows the fields somebody decided to lay out. This shows all of them, flat, with
 * their API names — the question it answers is "what is actually on this record", which a layout is
 * specifically designed not to answer.
 *
 * Editing is EXPLICIT, which is the opposite of the record page's inline editing and deliberately
 * so. A developer poking at four fields of a live record wants to see the whole set of changes
 * before any of them lands. Nothing is written on blur; Save carries only what changed, so a save
 * here can never quietly overwrite a field somebody else moved in the meantime.
 */
const docs: ComponentDocs = {
  aliases: ['record data', 'field inspector', 'all fields', 'record fields table', 'raw record view'],

  answers: [
    'see every field on a record, not just the laid-out ones',
    'find a field on a record by its API name',
    'change a few fields and save them together',
    'see which fields on a record are actually empty',
    'find out why a field cannot be edited',
  ],

  placements: ['appPage', 'recordPage'],

  whenToUse:
    'When somebody needs the WHOLE record — API names included — rather than the fields a layout ' +
    'chose to show. Debugging, data fixing, and answering "is that field even populated".',

  insteadUse: [
    {
      name: 'FieldRenderer',
      when:
        'the fields are being presented to a person doing their job, where the layout, the labels ' +
        'and the typed controls are the point. This is the developer’s flat view of the same record.',
    },
    {
      name: 'KeyValueRow',
      when: 'a handful of named values are being read and nothing is being edited.',
    },
  ],

  inputs: [
    {
      name: 'fields',
      type: 'json',
      description:
        'Every field of the record: `{ apiName, label, type, value?, lock? }[]`. `lock` is why a ' +
        'field cannot be edited — `computed`, `no-permission` or `masked` — and each reads as its own ' +
        'sentence in the row, because those three need different things from the reader.',
      default: [],
    },
    {
      name: 'recordId',
      attribute: 'record-id',
      type: 'recordId',
      source: 'urlParameter',
      description:
        'Which record this is. Changing it CLEARS the pending edits — carrying them across would ' +
        'write one record’s half-finished changes onto another.',
    },
    {
      name: 'search',
      attribute: 'search',
      type: 'text',
      description: 'Narrows the rows by API name, label or type. Reflected, so it can be linked to.',
    },
    {
      name: 'saving',
      attribute: 'saving',
      type: 'boolean',
      description: 'A save is in flight: Save reads "Saving…" and every action is held.',
      default: false,
    },
    {
      name: 'deletable',
      attribute: 'deletable',
      type: 'boolean',
      description:
        'Show the delete control. Absent ⇒ hidden rather than disabled: a control for something this ' +
        'person cannot do is a question they should not have to ask.',
      default: false,
    },
    {
      name: 'sortKey',
      attribute: 'sort-key',
      type: 'text',
      options: ['apiName', 'label', 'type', 'value'],
      description:
        'Which of the four columns the rows are ordered by. All four matter: looking for a field by ' +
        'API name, by label, by type, and looking for which ones have values in them are four ' +
        'different searches, and a fixed order serves at most one.',
      default: 'apiName',
    },
    {
      name: 'sortDirection',
      attribute: 'sort-direction',
      type: 'text',
      options: ['asc', 'desc'],
      description: 'Which way. Empty values sort last in both directions.',
      default: 'asc',
    },
  ],

  events: [
    {
      name: 'caos-field-edit',
      when: 'A value is typed into. Fires for each change, including one that returns a field to its original.',
      carries: '`{ apiName, value, dirty }`.',
    },
    {
      name: 'caos-record-save',
      when: 'Save is pressed. Never while `saving`, and never with nothing changed.',
      carries:
        '`{ recordId, changes }` — `changes` holds ONLY the fields that changed. A component handing ' +
        'back every field it was given would make every save a full overwrite.',
    },
    { name: 'caos-record-discard', when: 'Discard is pressed with changes pending.', carries: 'nothing' },
    {
      name: 'caos-record-export',
      when: 'Export is pressed.',
      carries: '`{ recordId, fields }` — the fields as currently filtered and ordered.',
    },
    {
      name: 'caos-record-delete',
      when: 'The delete control is pressed.',
      carries:
        '`{ recordId }`. It ASKS: whether a record can be deleted, and what warning that deserves, is ' +
        'not a component’s call, and an irreversible act behind an unconfirmed click is not one either.',
    },
  ],

  state: [
    {
      name: 'pending',
      type: '{ [apiName]: string }',
      description: 'The changed fields and their new values. Empty when nothing is pending.',
    },
    {
      name: 'dirty',
      type: 'boolean',
      description:
        'Whether anything is pending. A comparison against the values handed in, not a flag somebody ' +
        'sets — so typing a value and typing the original back leaves it false.',
    },
    { name: 'visibleFields', type: 'CaosRecordField[]', description: 'The fields after the search, in the current order.' },
  ],

  methods: [
    {
      name: 'accept',
      description:
        'Clear the pending edits. What a host calls once a save has actually landed — the component ' +
        'does not assume its own event succeeded.',
      args: [],
    },
  ],

  parts: [
    { part: 'inspector', is: 'The outer column.' },
    { part: 'toolbar', is: 'The search and action row.' },
    { part: 'search', is: 'The search box.' },
    { part: 'actions', is: 'The save / discard / export / delete cluster.' },
    { part: 'summary', is: 'The field count and unsaved-change line.' },
    { part: 'table', is: 'The composed data table.' },
    { part: 'value-input', is: 'The editor in one value cell.', when: 'The field is not locked.' },
    { part: 'reason', is: 'The text in a locked field’s value cell.', when: 'The field is locked.' },
  ],

  accessibility:
    'Every value editor carries an accessible name reading the label and the API name, so a row read ' +
    'aloud says which field is being edited rather than announcing an unlabelled text box. The ' +
    'summary is a `role="status"` live region, so the number of unsaved changes is announced as it ' +
    'moves — somebody editing down a long list is not looking at the header. A locked field shows ' +
    'the reason as text in its own cell rather than as a disabled control, because "you cannot" is ' +
    'the half a reader already worked out and "the platform computes it" is the half they need.',

  examples: [
    {
      key: 'record',
      label: 'A record with a change pending',
      summary:
        'The flat view: API name, label, type and value, with one field edited and not yet saved. ' +
        'Save and Discard are both enabled, and the summary counts the change.',
      placement: {
        component: 'caos-record-field-inspector',
        inputs: {
          'record-id': '001x00000A1b2C',
          deletable: true,
          fields: [
            { apiName: 'Id', label: 'Record Id', type: 'Id', value: '001x00000A1b2C', lock: 'computed' },
            { apiName: 'Name', label: 'Account Name', type: 'Text', value: 'Northwind Traders' },
            { apiName: 'Industry', label: 'Industry', type: 'Picklist', value: 'Retail' },
            { apiName: 'AnnualRevenue', label: 'Annual Revenue', type: 'Currency', value: 4200000 },
            { apiName: 'Website', label: 'Website', type: 'Url', value: null },
            { apiName: 'CreatedDate', label: 'Created Date', type: 'DateTime', value: '2025-03-04T09:11:00Z', lock: 'computed' },
          ],
        },
      },
      stage: { needsWidth: 780 },
    },
    {
      key: 'locked',
      label: 'The three reasons a field cannot be edited',
      summary:
        'Computed by the platform, not permitted to this person, and masked. Each says which, ' +
        'because they need different things from the reader and a greyed box says none of it.',
      placement: {
        component: 'caos-record-field-inspector',
        inputs: {
          'record-id': '001x00000A1b2C',
          fields: [
            { apiName: 'Name', label: 'Account Name', type: 'Text', value: 'Northwind Traders' },
            { apiName: 'LastModifiedDate', label: 'Last Modified', type: 'DateTime', value: '2026-09-02T14:00:00Z', lock: 'computed' },
            { apiName: 'CreditRating__c', label: 'Credit Rating', type: 'Text', value: 'A-', lock: 'no-permission' },
            { apiName: 'TaxId__c', label: 'Tax Id', type: 'Text', value: '99-1234567', lock: 'masked' },
          ],
        },
      },
      stage: { needsWidth: 780 },
    },
  ],
};

export default docs;
